import type { ApiError, ExchangeRates, Kanji, KanjiProgress, KanjiProgressPut, QuranSurah, QuranSurahPage, QuranVerse } from '@obv/api-client';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { keys } from './keys';

const pendingRetry = (code: string) => (count: number, err: ApiError) =>
  err.code === code ? count < 30 : err.status === 0 || err.status >= 500 ? count < 3 : false;

export const QURAN_PAGE_SIZE = 20;

export function useQuranSurahs(enabled = true) {
  return useQuery<QuranSurah[], ApiError>({
    queryKey: keys.quran.surahs(),
    queryFn: () => api.quran.surahs().then((r) => r.items),
    enabled,
    staleTime: Infinity,
    cacheTime: Infinity,
  });
}

/** A surah in 20-ayah pages, loaded as the reader scrolls. Kept in memory only. */
export function useQuranSurah(surah: number | null, translation: 'en' | 'bn') {
  return useInfiniteQuery<QuranSurahPage, ApiError>({
    queryKey: keys.quran.page(surah || 0, translation),
    queryFn: ({ pageParam = 1 }) => api.quran.surah(surah!, { translation, from: pageParam as number, limit: QURAN_PAGE_SIZE }),
    getNextPageParam: (last) => last.next_from || undefined,
    enabled: surah !== null,
    staleTime: Infinity,
    cacheTime: 10 * 60_000,
    meta: { persist: false },
  });
}

export function useVerseOfDay(date: string, translation: 'en' | 'bn', enabled = true) {
  return useQuery<QuranVerse, ApiError>({
    queryKey: keys.quran.verse(date, translation),
    queryFn: () => api.quran.verseOfDay(translation),
    enabled,
    staleTime: Infinity,
  });
}

export function useExchangeRates(enabled = true) {
  return useQuery<ExchangeRates, ApiError>({
    queryKey: keys.exchange(),
    queryFn: () => api.exchange.rates(),
    enabled,
    staleTime: 30 * 60_000,
    refetchInterval: 60 * 60_000,
    retry: pendingRetry('exchange.pending'),
    retryDelay: (_n, err) => (err.code === 'exchange.pending' ? 10_000 : 5_000),
  });
}

/** All 253 kanji (~60 KB gzipped). Memory only; the slideshow and the study page share it. */
export function useKanji(enabled = true) {
  return useQuery<Kanji[], ApiError>({
    queryKey: keys.kanji.list(),
    queryFn: () => api.kanji.list().then((r) => r.items),
    enabled,
    staleTime: Infinity,
    cacheTime: Infinity,
    meta: { persist: false },
  });
}

export function useKanjiProgress(enabled = true) {
  return useQuery<KanjiProgress[], ApiError>({
    queryKey: keys.kanji.progress(),
    queryFn: () => api.kanji.progress().then((r) => r.items),
    enabled,
  });
}

export function useSetKanjiProgress() {
  const qc = useQueryClient();
  return useMutation<KanjiProgress, ApiError, { id: number; body: KanjiProgressPut }>({
    mutationFn: (v) => api.kanji.setProgress(v.id, v.body),
    onMutate: (v) => {
      qc.setQueryData<KanjiProgress[]>(keys.kanji.progress(), (old) => {
        const list = old || [];
        const cur = list.find((p) => p.kanji_id === v.id);
        const next = { kanji_id: v.id, learned: false, bookmarked: false, updated_at: new Date().toISOString(), ...cur, ...v.body } as KanjiProgress;
        return cur ? list.map((p) => (p.kanji_id === v.id ? next : p)) : [...list, next];
      });
    },
    onSettled: () => qc.invalidateQueries({ queryKey: keys.kanji.progress() }),
  });
}
